import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, TrendingUp, ChevronDown, ChevronLeft, ChevronRight, Edit2, Link2, Star, PlusCircle, MinusCircle } from 'lucide-react';
import OptionsChartWidget from './DashboardBuilder/OptionsChartWidget';
import AdvancedPriceChartWidget from './DashboardBuilder/AdvancedPriceChartWidget';
import CategoryAccordion from './DashboardBuilder/CategoryAccordion';
import IndicatorSettingsModal from './DashboardBuilder/IndicatorSettingsModal';
import { formatIndicatorDisplay, mergeCustomIndicators } from '../utils/indicatorCategories';
import { useCustomIndicators } from '../contexts/CustomIndicatorContext';
import { INDICATOR_DEFAULTS } from '../utils/indicatorDefaults';
import { PnLSimulatorContent } from './PnLSimulatorModal';
import GreeksMarketSection from './GreeksMarketSection';
import { findBreakevenPrice } from '../utils/blackScholes';
import GreeksTransitionSection, { formatGreekNum } from './GreeksTransitionSection';
import { getSimpleStrikePositionLabel } from '../utils/presetMappings';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { mobileColors, colorFor, formatColumnPct, formatHeatPct } from '../theme/mobileTokens';
import { calculateDaysUntilExpiration } from '../utils/screenerDataHelpers';
import { isLiveQuote, getIvTooltip } from '../utils/dataSourceTooltip';

/**
 * OptionsPremiumChartModal - Unified modal with three views:
 *  1. "Historical Charts" — side-by-side premium & underlying historical charts
 *  2. "Simulated Returns" — P&L simulator with sliders
 *  3. "Greeks"            — snapshot row + GreeksMarketSection
 *
 * Props:
 * - isOpen, onClose, ticker, optionType, strike, expiration (existing)
 * - rowData: full screener row, used by simulated returns and greeks tabs
 * - defaultView: 'charts' | 'simulated' | 'greeks' — which tab opens first
 */
const MODAL_INDICATORS_STORAGE_KEY = 'premiumChartModal_indicators';
const MODAL_SHOW_BOLLINGER_KEY = 'premiumChartModal_showBollinger';
const MODAL_INDICATOR_SETTINGS_KEY = 'premiumChartModal_indicatorSettings';
const MODAL_LAST_SCENARIO_KEY = 'premiumChartModal_lastScenarioPct';

// --- Helpers reused by the Details tab body ---
const formatMoneyVal = (v, opts) => {
  if (v == null || isNaN(v)) return '--';
  return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', ...opts });
};


const addDaysToToday = (days) => {
  if (days == null || !isFinite(Number(days))) return null;
  const d = new Date();
  d.setDate(d.getDate() + Number(days));
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const DeltaChip = ({ pct }) => {
  if (pct == null || isNaN(pct)) return null;
  const { bg, fg } = colorFor(pct);
  return (
    <span style={{
      background: bg,
      color: fg,
      padding: '2px 8px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {formatHeatPct(pct)}
    </span>
  );
};

const actionBtnStyle = (variant) => ({
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${
    variant === 'favorited' ? '#facc15'
    : variant === 'danger' ? 'rgba(239,68,68,0.5)'
    : 'rgba(255,255,255,0.12)'
  }`,
  background: variant === 'favorited' ? 'rgba(250,204,21,0.15)'
            : variant === 'danger' ? 'rgba(239,68,68,0.1)'
            : 'transparent',
  color: variant === 'favorited' ? '#facc15'
       : variant === 'danger' ? '#fca5a5'
       : mobileColors.textPrimary,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const TransitionRow = ({ label, before, after, deltaPct }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '10px 14px',
    borderBottom: `0.5px solid ${mobileColors.borderHair}`,
  }}>
    <div style={{ fontSize: 11, color: mobileColors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 15, color: mobileColors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
        {before}
      </span>
      <span style={{ fontSize: 14, color: mobileColors.textTertiary }}>→</span>
      <span style={{ fontSize: 15, color: mobileColors.textPrimary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {after}
      </span>
      <span style={{ marginLeft: 'auto' }}>
        <DeltaChip pct={deltaPct} />
      </span>
    </div>
  </div>
);

/**
 * Horizontal-scrollable scenario strip. Renders one chip per scenario %
 * present in row._heatmap, colored by its premiumGain. The chip matching
 * `selectedPct` is highlighted.
 */
function ScenarioStrip({ row, selectedPct, onSelect }) {
  if (!row || !row._heatmap) return null;
  const pctKeys = Object.keys(row._heatmap)
    .map(k => Number(k))
    .filter(n => !isNaN(n))
    .sort((a, b) => a - b);
  if (pctKeys.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      gap: 6,
      overflowX: 'auto',
      padding: '10px 14px',
      borderBottom: `0.5px solid ${mobileColors.borderHair}`,
      WebkitOverflowScrolling: 'touch',
    }}>
      {pctKeys.map(pct => {
        const cell = row._heatmap[pct];
        const gain = cell?.premiumGain;
        const { bg, fg } = colorFor(gain);
        const isActive = Number(selectedPct) === pct;
        return (
          <button
            key={pct}
            type="button"
            onClick={() => onSelect(pct)}
            style={{
              flex: '0 0 auto',
              minWidth: 64,
              padding: '6px 10px',
              borderRadius: 8,
              background: bg,
              color: fg,
              border: isActive ? '2px solid #218aff' : '1px solid rgba(255,255,255,0.08)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              transform: isActive ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.12s ease, border-color 0.12s ease',
              fontFamily: 'inherit',
            }}
            aria-label={`Scenario ${formatColumnPct(pct)}${isActive ? ' (current)' : ''}`}
          >
            <span style={{ fontSize: 11, fontWeight: 600 }}>{formatColumnPct(pct)}</span>
            <span style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {formatHeatPct(gain)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * DetailsTabBody — cell-level detail for a (contract × scenario) pair.
 * Render the same on mobile and desktop; pure presentation.
 */
function DetailsTabBody({
  row,
  selectedScenarioPct,
  onSelectScenario,
  onToggleFavorite,
  onRemoveRow,
  onSaveToWatchlist,
  isFavorited = false,
  isSaving = false,
}) {
  if (!row) {
    return (
      <div className="flex items-center justify-center text-gray-500 text-sm py-8 text-center">
        Details not available. Open from a contract row.
      </div>
    );
  }

  const cell = row._heatmap?.[selectedScenarioPct];
  const premiumGain = cell?.premiumGain;
  const simulatedStock = cell?.simulatedStock;
  const projectedPremium = cell?.projectedPremium;

  const currentPremium = parseFloat(row['Current Premium']);
  const currentUnderlying = parseFloat(row['Underlying Price'] || row['Current Underlying']);
  const simulatedValueOf1000 = premiumGain != null && !isNaN(premiumGain)
    ? 1000 * (1 + premiumGain / 100)
    : null;

  const rawDaysToExit = row._daysToExit != null ? row._daysToExit : parseFloat(row['Days to Gain']);
  const daysToExit = !isNaN(rawDaysToExit) && rawDaysToExit != null ? Math.round(rawDaysToExit) : null;
  const exitDateStr = addDaysToToday(daysToExit);

  const rawIV = parseFloat(row['Implied Volatility']);
  const ivPct = !isNaN(rawIV) ? (rawIV > 5 ? rawIV : rawIV * 100) : null;
  // App IV vs Massive IV, and which one the projections use.
  const ivTip = getIvTooltip(row);
  const greeks = [
    { key: 'Delta', value: formatGreekNum(row['Delta'], 4) },
    { key: 'Gamma', value: formatGreekNum(row['Gamma'], 4) },
    { key: 'Theta', value: formatGreekNum(row['Theta'], 4) },
    { key: 'Vega',  value: formatGreekNum(row['Vega'],  4) },
    { key: 'Rho',   value: formatGreekNum(row['Rho'],   4) },
    { key: 'IV',    value: ivPct != null ? `${ivPct.toFixed(2)}%` : null, title: ivTip },
  ].filter(g => g.value !== null);

  // True when GreeksTransitionSection will render — it owns the Greeks block in
  // that case, and the quoted-value grid below stands down to avoid showing two
  // Greeks sections that disagree (ours is modeled, that one is the vendor's).
  const hasProjectedGreeks = !!(cell && cell.timeToTarget != null && cell.ivNow != null);

  const volume = parseFloat(row['Volume']);
  const hasVolume = !isNaN(volume) && volume > 0;

  return (
    <div>
      {(onToggleFavorite || onSaveToWatchlist || onRemoveRow) && (
        <div style={{
          display: 'flex',
          gap: 8,
          padding: '10px 14px',
          borderBottom: `0.5px solid ${mobileColors.borderHair}`,
        }}>
          {onToggleFavorite && (
            <button
              type="button"
              onClick={onToggleFavorite}
              aria-label={isFavorited ? 'Unfavorite' : 'Favorite'}
              style={actionBtnStyle(isFavorited ? 'favorited' : 'default')}
            >
              <Star size={14} fill={isFavorited ? 'currentColor' : 'none'} />
              <span>{isFavorited ? 'Favorited' : 'Favorite'}</span>
            </button>
          )}
          {onSaveToWatchlist && (
            <button
              type="button"
              onClick={onSaveToWatchlist}
              disabled={isSaving}
              aria-label="Save to watchlist"
              style={{ ...actionBtnStyle('default'), opacity: isSaving ? 0.5 : 1 }}
            >
              <PlusCircle size={14} />
              <span>{isSaving ? 'Saving…' : 'Save'}</span>
            </button>
          )}
          {onRemoveRow && (
            <button
              type="button"
              onClick={onRemoveRow}
              aria-label="Remove from results"
              style={actionBtnStyle('danger')}
            >
              <MinusCircle size={14} />
              <span>Drop</span>
            </button>
          )}
        </div>
      )}

      <ScenarioStrip
        row={row}
        selectedPct={selectedScenarioPct}
        onSelect={onSelectScenario}
      />

      {/* Exit timing caption */}
      <div style={{
        padding: '10px 14px',
        fontSize: 12,
        color: mobileColors.textSecondary,
        borderBottom: `0.5px solid ${mobileColors.borderHair}`,
      }}>
        {daysToExit != null && exitDateStr ? (
          <>
            Exit in{' '}
            <span style={{ color: mobileColors.textPrimary, fontWeight: 600 }}>{daysToExit} days</span>
            {' · '}
            <span style={{ color: mobileColors.textPrimary, fontWeight: 600 }}>{exitDateStr}</span>
          </>
        ) : (
          <>Exit: <span style={{ color: mobileColors.textPrimary, fontWeight: 600 }}>per-contract</span></>
        )}
      </div>

      <TransitionRow
        label="Premium"
        before={formatMoneyVal(currentPremium, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        after={formatMoneyVal(projectedPremium, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        deltaPct={premiumGain}
      />

      <TransitionRow
        label="Underlying"
        before={formatMoneyVal(currentUnderlying, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        after={formatMoneyVal(simulatedStock, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        deltaPct={Number(selectedScenarioPct)}
      />

      <TransitionRow
        label="$1,000 invested"
        before={formatMoneyVal(1000, { maximumFractionDigits: 0 })}
        after={formatMoneyVal(simulatedValueOf1000, { maximumFractionDigits: 0 })}
        deltaPct={premiumGain}
      />

      {/* Projected Greeks when the cell carries its pricing inputs; otherwise
          the original quoted-Greeks grid below. */}
      <GreeksTransitionSection cell={cell} ivTooltip={ivTip} />

      {!hasProjectedGreeks && greeks.length > 0 && (
        <div style={{ padding: '12px 14px', borderBottom: `0.5px solid ${mobileColors.borderHair}` }}>
          <div style={{
            fontSize: 11,
            color: mobileColors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 8,
          }}>
            Greeks
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {greeks.map(g => (
              <div key={g.key} style={{
                background: mobileColors.cardSurface,
                border: `0.5px solid ${mobileColors.borderSubtle}`,
                borderRadius: 8,
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}>
                <span style={{ fontSize: 10, color: mobileColors.textTertiary }}>{g.key}</span>
                <span
                  className={g.title ? 'iv-source-hint' : undefined}
                  title={g.title || undefined}
                  style={{ fontSize: 13, color: mobileColors.textPrimary, fontVariantNumeric: 'tabular-nums' }}
                >
                  {g.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasVolume && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 14px',
        }}>
          <span style={{ fontSize: 11, color: mobileColors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Volume
          </span>
          <span style={{ fontSize: 14, color: mobileColors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
            {volume.toLocaleString('en-US')}
          </span>
        </div>
      )}
    </div>
  );
}

export default function OptionsPremiumChartModal({
  isOpen,
  onClose,
  ticker,
  optionType,
  strike,
  expiration,
  rowData,
  defaultView = 'charts',
  // When opening from a heatmap cell tap, the parent passes the scenario % so
  // the Details tab can pre-select it. When omitted (e.g. strike-column tap),
  // we fall back to the sticky value persisted in localStorage.
  scenarioPct = null,
  // Pagination through neighbour contracts (mobile-only footer)
  onPrev,
  onNext,
  prevLabel,
  nextLabel,
  positionLabel,
  // Optional row actions surfaced inside the Details tab. All optional; the
  // action row in Details is hidden when none are provided.
  onToggleFavorite,
  onRemoveRow,
  onSaveToWatchlist,
  isFavorited = false,
  isSaving = false,
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [activeView, setActiveView] = useState(defaultView);

  // Currently selected scenario % for the Details tab. Sticky across opens
  // via localStorage. Read order on mount: explicit prop > localStorage > 0.
  const [selectedScenarioPct, setSelectedScenarioPct] = useState(() => {
    if (scenarioPct != null && !isNaN(Number(scenarioPct))) return Number(scenarioPct);
    try {
      const saved = localStorage.getItem(MODAL_LAST_SCENARIO_KEY);
      const n = saved != null ? Number(saved) : NaN;
      return isNaN(n) ? 0 : n;
    } catch { return 0; }
  });

  // Independent date ranges for side-by-side charts (desktop)
  const [premiumDateRange, setPremiumDateRange] = useState('3m');
  const [underlyingDateRange, setUnderlyingDateRange] = useState('1y');

  // Single shared range for the mobile stacked layout
  const [mobileDateRange, setMobileDateRange] = useState('3m');

  // Indicator state (persisted to localStorage)
  const [indicators, setIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem(MODAL_INDICATORS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const [showBollinger, setShowBollinger] = useState(() => {
    try {
      const saved = localStorage.getItem(MODAL_SHOW_BOLLINGER_KEY);
      return saved === 'true';
    } catch { return false; }
  });

  const [showIndicatorDropdown, setShowIndicatorDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [hoveredIndicator, setHoveredIndicator] = useState(null);

  const [indicatorSettings, setIndicatorSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(MODAL_INDICATOR_SETTINGS_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  // Crosshair sync state (Line em up). Default to linked on mobile so the
  // user gets a synchronized crosshair across both charts without having to
  // toggle (no Line-em-up button exists on the mobile layout). Desktop keeps
  // the existing opt-in default of `false`.
  const [chartsLinked, setChartsLinked] = useState(isMobile);
  const [crosshairDate, setCrosshairDate] = useState(null);
  const crosshairDebounceRef = useRef(null);

  const handleLineEmUp = () => {
    setUnderlyingDateRange(premiumDateRange);
    setChartsLinked(true);
  };

  const handleUnlinkCharts = () => {
    setChartsLinked(false);
    setCrosshairDate(null);
  };

  // Cross-chart data for enriched tooltips
  const [premiumDataAtCrosshair, setPremiumDataAtCrosshair] = useState(null);
  const [underlyingDataAtCrosshair, setUnderlyingDataAtCrosshair] = useState(null);

  const handleCrosshairMove = useCallback((date) => {
    if (crosshairDebounceRef.current) clearTimeout(crosshairDebounceRef.current);
    crosshairDebounceRef.current = setTimeout(() => {
      setCrosshairDate(date);
      if (!date) {
        setPremiumDataAtCrosshair(null);
        setUnderlyingDataAtCrosshair(null);
      }
    }, 50);
  }, []);

  const handlePremiumCrosshairData = useCallback((dataPoint) => {
    setPremiumDataAtCrosshair(dataPoint);
  }, []);

  const handleUnderlyingCrosshairData = useCallback((dataPoint) => {
    setUnderlyingDataAtCrosshair(dataPoint);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (crosshairDebounceRef.current) clearTimeout(crosshairDebounceRef.current); };
  }, []);

  const dropdownRef = useRef(null);
  const { customIndicators } = useCustomIndicators();
  const mergedCategories = mergeCustomIndicators(customIndicators);

  // Sync activeView when defaultView prop changes (new modal open). When the
  // parent passes an explicit scenarioPct (cell-tap entry), force the Details
  // tab + that scenario so the user lands where they expect.
  useEffect(() => {
    if (!isOpen) return;
    if (scenarioPct != null && !isNaN(Number(scenarioPct))) {
      setActiveView('details');
      setSelectedScenarioPct(Number(scenarioPct));
    } else {
      setActiveView(defaultView);
    }
  }, [isOpen, defaultView, scenarioPct]);

  // Persist the current scenario % so subsequent modal opens (without an
  // explicit prop) land on the same scenario.
  useEffect(() => {
    try { localStorage.setItem(MODAL_LAST_SCENARIO_KEY, String(selectedScenarioPct)); } catch {}
  }, [selectedScenarioPct]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowIndicatorDropdown(false);
      }
    };
    if (showIndicatorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showIndicatorDropdown]);

  // Persist to localStorage
  useEffect(() => {
    try { localStorage.setItem(MODAL_INDICATORS_STORAGE_KEY, JSON.stringify(indicators)); } catch {}
  }, [indicators]);

  useEffect(() => {
    try { localStorage.setItem(MODAL_SHOW_BOLLINGER_KEY, String(showBollinger)); } catch {}
  }, [showBollinger]);

  useEffect(() => {
    try { localStorage.setItem(MODAL_INDICATOR_SETTINGS_KEY, JSON.stringify(indicatorSettings)); } catch {}
  }, [indicatorSettings]);

  if (!isOpen) return null;

  // Extract live prices and timestamps from rowData (available from screener/watchlist table)
  const currentPremium = rowData ? parseFloat(rowData['Current Premium']) : null;
  const currentUnderlying = rowData
    ? parseFloat(rowData['Underlying Price'] || rowData['Current Underlying'])
    : null;
  // current_premium.timestamp is the time of the price rung used (quote time
  // when priced off the mid); .timeframe is the provider's DELAYED / REAL-TIME label.
  const premiumMeta = rowData?._meta?.current_premium || null;
  const premiumTimestamp = premiumMeta?.timestamp || null;
  const underlyingTimestamp = rowData?._meta?.underlying_price?.timestamp || null;

  // Widget for OptionsChartWidget — mobile uses the single shared range
  const optionsWidget = {
    id: 'modal-chart',
    ticker: ticker,
    config: {
      option_type: optionType?.toLowerCase(),
      strike: parseFloat(strike),
      expiration: expiration,
      date_range: isMobile ? mobileDateRange : premiumDateRange,
    },
  };

  // Build indicatorConfig for AdvancedPriceChartWidget
  const indicatorConfig = indicators.length > 0 && Object.keys(indicatorSettings).some(key => indicators.includes(key))
    ? indicators.reduce((config, ind) => {
        const defaultSettings = INDICATOR_DEFAULTS[ind] || {};
        const customSettings = indicatorSettings[ind] || {};
        config[ind] = {
          ...defaultSettings,
          ...customSettings,
          colors: { ...defaultSettings.colors, ...customSettings.colors }
        };
        return config;
      }, {})
    : null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleIndicatorToggle = (indicatorValue) => {
    const isCustom = indicatorValue.startsWith('custom_');
    let baseIndicator = indicatorValue;
    let customSettings = null;

    if (isCustom) {
      const customId = parseInt(indicatorValue.replace('custom_', ''));
      const customIndicator = customIndicators.find(ci => ci.id === customId);
      if (customIndicator) {
        baseIndicator = customIndicator.base_indicator;
        customSettings = customIndicator.settings;
      }
    }

    setIndicators(prev =>
      prev.includes(baseIndicator)
        ? prev.filter(i => i !== baseIndicator)
        : [...prev, baseIndicator]
    );

    if (isCustom && customSettings && !indicators.includes(baseIndicator)) {
      setIndicatorSettings(prev => ({ ...prev, [baseIndicator]: customSettings }));
    }

    if (baseIndicator === 'bollinger' && !indicators.includes('bollinger')) {
      setShowBollinger(true);
    }
  };

  const handleOpenSettings = (indicator) => {
    setSelectedIndicator(indicator);
    setShowSettingsModal(true);
    setShowIndicatorDropdown(false);
  };

  const handleSaveSettings = async (indicator, settings) => {
    setIndicatorSettings({ ...indicatorSettings, [indicator]: settings });
  };

  // Toggle button style helper
  const toggleStyle = (isActive) => ({
    padding: '8px 16px',
    borderRadius: '6px',
    border: isActive ? '2px solid #218aff' : '1px solid #333',
    background: isActive ? '#218aff20' : '#0a0a0a',
    color: isActive ? '#ffffff' : '#b0b0b0',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  });

  // --- Helpers for the mobile identity block ---
  const formatStrikeBadge = () => {
    const num = parseFloat(strike);
    const suffix = optionType?.toLowerCase() === 'put' ? 'P' : 'C';
    if (isNaN(num)) return `$${strike}${suffix}`;
    return num % 1 === 0 ? `$${num.toFixed(0)}${suffix}` : `$${num.toFixed(2)}${suffix}`;
  };

  // Live vs Delayed comes from the provider's timeframe label when present
  // (Massive reports DELAYED until the market-data agreements are complete).
  // Without one, keep the old rule: unknown source or Polygon ("P") is Live,
  // EODHD ("E") is Delayed.
  const isLive = isLiveQuote(premiumMeta);

  const strikePosLabel = rowData
    ? getSimpleStrikePositionLabel(rowData['Strike Position'], optionType)
    : null;

  const daysToExp = rowData?.['Days Until Expiration'];
  const daysToExpDisplay = (() => {
    if (daysToExp != null && !isNaN(parseFloat(daysToExp))) return Math.round(parseFloat(daysToExp));
    if (!expiration) return null;
    try {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const exp = new Date(expiration); exp.setHours(0, 0, 0, 0);
      return Math.max(0, Math.ceil((exp - today) / (1000 * 60 * 60 * 24)));
    } catch { return null; }
  })();

  // Mobile range picker options. "All" maps to the widget's max range ('5y').
  const mobileRangeOptions = [
    { value: '1m', label: '1M' },
    { value: '3m', label: '3M' },
    { value: '6m', label: '6M' },
    { value: '1y', label: '1Y' },
    { value: '5y', label: 'All' },
  ];

  // Render the indicators control (trigger + dropdown). On desktop it lives in
  // the header; on mobile it lives inside the underlying chart card. Markup is
  // identical — only the parent changes.
  const renderIndicatorsControl = (anchor = 'header') => (
    <div className="relative" ref={anchor === 'header' ? dropdownRef : null}>
      <button
        onClick={() => setShowIndicatorDropdown(!showIndicatorDropdown)}
        className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition flex items-center gap-1"
        title="Indicators (Underlying)"
      >
        <span>Indicators</span>
        {indicators.length > 0 && (
          <span className="px-1.5 py-0.5 bg-blue-600/30 text-blue-400 rounded text-[10px]">
            {indicators.length}
          </span>
        )}
        <ChevronDown className="w-3 h-3" />
      </button>

      {showIndicatorDropdown && (
        <div
          className={`absolute ${anchor === 'header' ? 'top-full right-0' : 'bottom-full left-0'} mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50 min-w-64 max-h-80 overflow-y-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-gray-700">
            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Active Indicators</p>
            <div className="space-y-1">
              {indicators.length > 0 ? (
                indicators.map(ind => (
                  <div
                    key={ind}
                    className="flex items-center justify-between text-sm text-gray-200 px-2 py-1 rounded hover:bg-gray-700/50 group"
                    onMouseEnter={() => setHoveredIndicator(ind)}
                    onMouseLeave={() => setHoveredIndicator(null)}
                  >
                    <button
                      onClick={() => handleOpenSettings(ind)}
                      className="flex-1 text-left hover:text-blue-400 transition-colors"
                      title="Click to edit settings"
                    >
                      {formatIndicatorDisplay(ind, indicatorSettings[ind])}
                    </button>
                    <div className="flex items-center gap-1">
                      {hoveredIndicator === ind && (
                        <button
                          onClick={() => handleOpenSettings(ind)}
                          className="text-gray-400 hover:text-white transition-colors px-1"
                          title="Edit settings"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleIndicatorToggle(ind)}
                        className="text-gray-400 hover:text-red-400 text-xs"
                        title="Remove indicator"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-500">None</span>
              )}
            </div>
          </div>
          {indicators.includes('bollinger') && (
            <div className="px-3 py-2 border-b border-gray-700">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showBollinger}
                  onChange={(e) => setShowBollinger(e.target.checked)}
                  className="w-3 h-3"
                />
                <span className="text-xs text-gray-300">Show Bollinger Bands</span>
              </label>
            </div>
          )}
          <div className="px-3 py-2">
            <p className="text-xs text-gray-400 mb-2">Add Indicators:</p>
            <CategoryAccordion
              categories={mergedCategories}
              selectedItems={indicators}
              onToggle={handleIndicatorToggle}
              onEdit={handleOpenSettings}
              displayMode="compact"
            />
          </div>
        </div>
      )}
    </div>
  );

  // --- Mobile layout (vertical, stacked) ---
  if (isMobile) {
    const tabPillStyle = (isActive, isDisabled) => ({
      padding: '10px 8px',
      borderRadius: '8px',
      border: isActive ? '2px solid #218aff' : '1px solid #333',
      background: isActive ? '#218aff20' : '#0a0a0a',
      color: isActive ? '#ffffff' : '#b0b0b0',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      fontWeight: '600',
      fontSize: '13px',
      opacity: isDisabled ? 0.4 : 1,
      transition: 'all 0.2s ease',
    });

    const rangeButtonStyle = (isActive) => ({
      padding: '6px 14px',
      borderRadius: '999px',
      border: isActive ? '1px solid #218aff' : '1px solid #333',
      background: isActive ? 'rgba(33, 138, 255, 0.15)' : '#1a1a1a',
      color: isActive ? '#218aff' : '#b0b0b0',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 500,
      flexShrink: 0,
    });

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-80 flex items-stretch justify-center"
        style={{ zIndex: 9999 }}
        onClick={handleOverlayClick}
      >
        <div className="bg-surface w-full flex flex-col" style={{ maxHeight: '100vh' }}>
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 flex-shrink-0">
            <span className="text-xs uppercase tracking-wide text-gray-500">Results</span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition p-1 rounded hover:bg-gray-700"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tab pills (3 equal-width) — moved above identity block so the
              tabs are visible at the very top of the modal body, acting as
              primary sub-nav. */}
          <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setActiveView('charts')} style={tabPillStyle(activeView === 'charts', false)}>
                Charts
              </button>
              <button
                onClick={() => rowData && setActiveView('simulated')}
                style={tabPillStyle(activeView === 'simulated', !rowData)}
                disabled={!rowData}
              >
                Returns
              </button>
              <button
                onClick={() => rowData && setActiveView('details')}
                style={tabPillStyle(activeView === 'details', !rowData)}
                disabled={!rowData}
              >
                Details
              </button>
            </div>
          </div>

          {/* Identity block */}
          <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-lg font-bold text-white">
                {ticker} {formatStrikeBadge()}
              </span>
              {rowData && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide border ${
                    optionType?.toLowerCase() === 'call'
                      ? 'bg-green-500/15 text-green-400 border-green-500/30'
                      : 'bg-red-500/15 text-red-400 border-red-500/30'
                  }`}
                >
                  {(rowData['Position Type'] || 'Buy').toUpperCase()}
                  {' · '}
                  {(optionType || '').toUpperCase()}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  isLive
                    ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                    : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${isLive ? 'bg-green-400' : 'bg-amber-400'}`}
                />
                {isLive ? 'Live' : 'Delayed'}
              </span>
            </div>
            <div className="text-xs text-gray-200 mt-1.5">
              {expiration}{daysToExpDisplay != null ? ` · ${daysToExpDisplay}d to expiry` : ''}
            </div>
            <div className="text-sm text-gray-200 mt-0.5">
              {currentPremium != null && !isNaN(currentPremium) && `Premium $${currentPremium.toFixed(2)}`}
              {strikePosLabel && ` · Strike ${strikePosLabel}`}
              {currentUnderlying != null && !isNaN(currentUnderlying) && ` · Underlying $${currentUnderlying.toFixed(2)}`}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            {activeView === 'charts' && (
              <div className="p-4 space-y-3">
                {/* Shared range picker + Indicators trigger. The Indicators
                    button sits at the top here (always visible) instead of
                    inside the Underlying Price card footer, which used to get
                    clipped off-card when several indicator subplots were
                    active. */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 flex-shrink-0">Range</span>
                  <div className="flex gap-1 overflow-x-auto flex-1">
                    {mobileRangeOptions.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setMobileDateRange(value)}
                        style={rangeButtonStyle(mobileDateRange === value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex-shrink-0">
                    {renderIndicatorsControl('header')}
                  </div>
                </div>

                {/* Options Premium card. Taller height (360) on mobile so the
                    flex-[3]/flex-[1] price/volume split leaves enough room
                    for the volume bars and the x-axis date labels below. */}
                <div className="bg-gray-950 rounded overflow-hidden flex flex-col" style={{ height: 360 }}>
                  <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-800 font-medium flex justify-between items-center">
                    <span>Options Premium</span>
                    <span className="text-gray-500">{mobileRangeOptions.find(r => r.value === mobileDateRange)?.label}</span>
                  </div>
                  <div className="flex-1 min-h-0">
                    <OptionsChartWidget
                      widget={optionsWidget}
                      livePrice={currentPremium}
                      liveTimestamp={premiumTimestamp}
                    />
                  </div>
                </div>

                {/* Underlying Price card. Definite outer height so
                    AdvancedPriceChartWidget's `h-full` + Recharts
                    ResponsiveContainer can resolve. 400px gives RSI/AC
                    sub-panels enough room to draw their lines/bars. The
                    Indicators picker lives at the top alongside the Range
                    chips — see comment above. */}
                <div className="bg-gray-950 rounded flex flex-col" style={{ height: 400 }}>
                  <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-800 font-medium flex justify-between items-center">
                    <span>Underlying Price</span>
                    <span className="text-gray-500">{mobileRangeOptions.find(r => r.value === mobileDateRange)?.label}</span>
                  </div>
                  <div className="flex-1 min-h-0">
                    <AdvancedPriceChartWidget
                      ticker={ticker}
                      dateRange={mobileDateRange}
                      indicators={indicators}
                      indicatorConfig={indicatorConfig}
                      showBollinger={showBollinger}
                      setShowBollinger={setShowBollinger}
                      livePrice={currentUnderlying}
                      liveTimestamp={underlyingTimestamp}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeView === 'simulated' && (
              <div className="h-full">
                {rowData ? (
                  <PnLSimulatorContent rowData={rowData} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 p-4 text-center text-sm">
                    Simulated returns not available. Open from a contract row.
                  </div>
                )}
              </div>
            )}

            {activeView === 'details' && (
              <DetailsTabBody
                row={rowData}
                selectedScenarioPct={selectedScenarioPct}
                onSelectScenario={setSelectedScenarioPct}
                onToggleFavorite={onToggleFavorite}
                onRemoveRow={onRemoveRow}
                onSaveToWatchlist={onSaveToWatchlist}
                isFavorited={isFavorited}
                isSaving={isSaving}
              />
            )}
          </div>

          {/* Pagination footer */}
          {(onPrev || onNext || positionLabel) && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800 flex-shrink-0 gap-2">
              <button
                onClick={onPrev}
                disabled={!onPrev}
                aria-label="Previous contract"
                className="flex items-center justify-center px-3 py-1.5 rounded border border-gray-700 bg-gray-900 text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500 hover:text-white transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500">{positionLabel}</span>
              <button
                onClick={onNext}
                disabled={!onNext}
                aria-label="Next contract"
                className="flex items-center justify-center px-3 py-1.5 rounded border border-gray-700 bg-gray-900 text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500 hover:text-white transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Indicator Settings Modal */}
        {showSettingsModal && selectedIndicator && (
          <IndicatorSettingsModal
            isOpen={showSettingsModal}
            onClose={() => {
              setShowSettingsModal(false);
              setSelectedIndicator(null);
            }}
            onSave={handleSaveSettings}
            indicator={selectedIndicator}
            currentSettings={indicatorSettings[selectedIndicator] || {}}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
      style={{ zIndex: 9999 }}
      onClick={handleOverlayClick}
    >
      <div className="bg-surface rounded-lg w-full max-w-5xl h-[700px] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          {/* Left: Contract info */}
          <div className="flex items-center gap-3">
            <span className="font-semibold text-text">{ticker}</span>
            <span className={`flex items-center gap-1 text-xs font-medium ${
              optionType?.toLowerCase() === 'call' ? 'text-green-400' : 'text-red-400'
            }`}>
              <TrendingUp className="w-3.5 h-3.5" />
              {optionType?.toUpperCase()}
            </span>
            <span className="text-gray-400 text-sm">${strike}</span>
            <span className="text-gray-500 text-sm">{expiration}</span>
          </div>

          {/* Center: Toggle */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setActiveView('charts')} style={toggleStyle(activeView === 'charts')}>
              Historical Charts
            </button>
            <button
              onClick={() => setActiveView('simulated')}
              style={{
                ...toggleStyle(activeView === 'simulated'),
                opacity: rowData ? 1 : 0.4,
                pointerEvents: rowData ? 'auto' : 'none',
              }}
              title={rowData ? 'Simulated Returns' : 'Not available — open from Premium % Gain'}
            >
              Simulated Returns
            </button>
            <button
              onClick={() => setActiveView('details')}
              style={{
                ...toggleStyle(activeView === 'details'),
                opacity: rowData ? 1 : 0.4,
                pointerEvents: rowData ? 'auto' : 'none',
              }}
              title={rowData ? 'Scenario details & Greeks' : 'Not available — open from a contract row'}
            >
              Details
            </button>
          </div>

          {/* Right: Controls + Close */}
          <div className="flex items-center gap-3">
            {/* Charts view controls */}
            {activeView === 'charts' && (
              <>
                {/* Premium date range */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">P:</span>
                  <select
                    value={premiumDateRange}
                    onChange={(e) => { setPremiumDateRange(e.target.value); handleUnlinkCharts(); }}
                    className="bg-background text-text text-xs px-1.5 py-1 rounded border border-muted hover:border-primary transition"
                  >
                    <option value="1m">1M</option>
                    <option value="3m">3M</option>
                    <option value="6m">6M</option>
                    <option value="1y">1Y</option>
                  </select>
                </div>

                {/* Line em up button */}
                <button
                  onClick={chartsLinked ? handleUnlinkCharts : handleLineEmUp}
                  className={`text-xs px-2.5 py-1 rounded border transition flex items-center gap-1.5 ${
                    chartsLinked
                      ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                      : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-blue-500 hover:text-white'
                  }`}
                  title={chartsLinked ? 'Charts are linked - crosshair synced' : 'Sync charts and enable crosshair'}
                >
                  <Link2 className="w-3.5 h-3.5" />
                  <span>{chartsLinked ? 'Linked' : 'Line em up'}</span>
                </button>

                {/* Underlying date range */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">U:</span>
                  <select
                    value={underlyingDateRange}
                    onChange={(e) => { setUnderlyingDateRange(e.target.value); handleUnlinkCharts(); }}
                    className="bg-background text-text text-xs px-1.5 py-1 rounded border border-muted hover:border-primary transition"
                  >
                    <option value="1m">1M</option>
                    <option value="3m">3M</option>
                    <option value="6m">6M</option>
                    <option value="1y">1Y</option>
                    <option value="2y">2Y</option>
                    <option value="5y">5Y</option>
                  </select>
                </div>

                {/* Indicators Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowIndicatorDropdown(!showIndicatorDropdown)}
                    className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition flex items-center gap-1"
                    title="Indicators (Underlying)"
                  >
                    <span>Indicators</span>
                    {indicators.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-blue-600/30 text-blue-400 rounded text-[10px]">
                        {indicators.length}
                      </span>
                    )}
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {showIndicatorDropdown && (
                    <div
                      className="absolute top-full right-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50 min-w-64 max-h-80 overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Active Indicators */}
                      <div className="px-3 py-2 border-b border-gray-700">
                        <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Active Indicators</p>
                        <div className="space-y-1">
                          {indicators.length > 0 ? (
                            indicators.map(ind => (
                              <div
                                key={ind}
                                className="flex items-center justify-between text-sm text-gray-200 px-2 py-1 rounded hover:bg-gray-700/50 group"
                                onMouseEnter={() => setHoveredIndicator(ind)}
                                onMouseLeave={() => setHoveredIndicator(null)}
                              >
                                <button
                                  onClick={() => handleOpenSettings(ind)}
                                  className="flex-1 text-left hover:text-blue-400 transition-colors"
                                  title="Click to edit settings"
                                >
                                  {formatIndicatorDisplay(ind, indicatorSettings[ind])}
                                </button>
                                <div className="flex items-center gap-1">
                                  {hoveredIndicator === ind && (
                                    <button
                                      onClick={() => handleOpenSettings(ind)}
                                      className="text-gray-400 hover:text-white transition-colors px-1"
                                      title="Edit settings"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleIndicatorToggle(ind)}
                                    className="text-gray-400 hover:text-red-400 text-xs"
                                    title="Remove indicator"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                        </div>
                      </div>

                      {/* Bollinger Toggle */}
                      {indicators.includes('bollinger') && (
                        <div className="px-3 py-2 border-b border-gray-700">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={showBollinger}
                              onChange={(e) => setShowBollinger(e.target.checked)}
                              className="w-3 h-3"
                            />
                            <span className="text-xs text-gray-300">Show Bollinger Bands</span>
                          </label>
                        </div>
                      )}

                      {/* Available Indicators */}
                      <div className="px-3 py-2">
                        <p className="text-xs text-gray-400 mb-2">Add Indicators:</p>
                        <CategoryAccordion
                          categories={mergedCategories}
                          selectedItems={indicators}
                          onToggle={handleIndicatorToggle}
                          onEdit={handleOpenSettings}
                          displayMode="compact"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition p-1 rounded hover:bg-gray-700"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {activeView === 'charts' && (
            /* Side-by-side Premium & Underlying charts */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 h-full">
              {/* Left: Options Premium Chart */}
              <div className="bg-gray-950 rounded overflow-hidden flex flex-col">
                <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-800 font-medium">
                  Options Premium
                </div>
                <div className="flex-1 min-h-0">
                  <OptionsChartWidget
                    widget={optionsWidget}
                    isLinked={chartsLinked}
                    onCrosshairMove={handleCrosshairMove}
                    sharedCrosshair={crosshairDate}
                    onCrosshairData={handlePremiumCrosshairData}
                    crossChartData={underlyingDataAtCrosshair}
                    livePrice={currentPremium}
                    liveTimestamp={premiumTimestamp}
                  />
                </div>
              </div>

              {/* Right: Underlying Price Chart */}
              <div className="bg-gray-950 rounded overflow-hidden flex flex-col">
                <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-800 font-medium">
                  Underlying Price
                </div>
                <div className="flex-1 min-h-0">
                  <AdvancedPriceChartWidget
                    ticker={ticker}
                    dateRange={underlyingDateRange}
                    indicators={indicators}
                    indicatorConfig={indicatorConfig}
                    isLinked={chartsLinked}
                    onCrosshairMove={handleCrosshairMove}
                    crosshairDate={crosshairDate}
                    showBollinger={showBollinger}
                    setShowBollinger={setShowBollinger}
                    onCrosshairData={handleUnderlyingCrosshairData}
                    crossChartData={premiumDataAtCrosshair}
                    livePrice={currentUnderlying}
                    liveTimestamp={underlyingTimestamp}
                  />
                </div>
              </div>
            </div>
          )}

          {activeView === 'simulated' && (
            /* Simulated Returns — P&L Simulator */
            <div className="h-full overflow-y-auto">
              {rowData ? (
                <PnLSimulatorContent rowData={rowData} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>Simulated returns not available. Click "Premium % Gain" on a contract to access this view.</p>
                </div>
              )}
            </div>
          )}

          {activeView === 'details' && (
            /* Details — per-(contract × scenario) panel */
            <div className="h-full overflow-y-auto">
              <DetailsTabBody
                row={rowData}
                selectedScenarioPct={selectedScenarioPct}
                onSelectScenario={setSelectedScenarioPct}
                onToggleFavorite={onToggleFavorite}
                onRemoveRow={onRemoveRow}
                onSaveToWatchlist={onSaveToWatchlist}
                isFavorited={isFavorited}
                isSaving={isSaving}
              />
            </div>
          )}
        </div>
      </div>

      {/* Indicator Settings Modal */}
      {showSettingsModal && selectedIndicator && (
        <IndicatorSettingsModal
          isOpen={showSettingsModal}
          onClose={() => {
            setShowSettingsModal(false);
            setSelectedIndicator(null);
          }}
          onSave={handleSaveSettings}
          indicator={selectedIndicator}
          currentSettings={indicatorSettings[selectedIndicator] || {}}
        />
      )}
    </div>
  );
}

/**
 * Greeks tab content: a 4-cell snapshot row (Underlying / Premium / Breakeven / DTE)
 * above the shared GreeksMarketSection. Reuses the watchlist-card's Greeks renderer
 * by passing a thin {position_type} adapter as `contract` and the full row as `simulationData`.
 */
function GreeksTab({ rowData, optionType, strike, hideSnapshot = false }) {
  const underlying = parseFloat(rowData['Underlying Price'] ?? rowData['Current Underlying']);
  const premium = parseFloat(rowData['Current Premium']);
  const dte = parseFloat(rowData['Days Until Expiration']);

  // Prefer the heatmap-attached breakeven; otherwise compute it via Black-Scholes.
  let breakeven = rowData._breakeven;
  if (breakeven == null || isNaN(breakeven)) {
    try {
      const k = parseFloat(strike);
      const rawIV = parseFloat(rowData['Implied Volatility']);
      const iv = rawIV > 5 ? rawIV / 100 : rawIV;
      const T = Math.max(dte / 365, 0.0001);
      const ot = optionType?.toLowerCase();
      const isSell = String(rowData['Position Type'] || '').toLowerCase() === 'sell';
      breakeven = findBreakevenPrice(k, premium, T, iv, ot, isSell);
    } catch {
      breakeven = null;
    }
  }

  const contractAdapter = {
    position_type: String(rowData['Position Type'] || 'buy').toLowerCase(),
  };

  const fmtPrice = (v) => (v != null && !isNaN(v) ? `$${Number(v).toFixed(2)}` : '--');
  const fmtDays = (v) => (v != null && !isNaN(v) ? `${Math.round(v)} d` : '--');

  return (
    <div className="space-y-4">
      {/* Snapshot row — suppressed on mobile because the identity block already shows these values */}
      {!hideSnapshot && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="bg-gray-800/50 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Underlying</div>
            <div className="text-sm font-semibold text-white">{fmtPrice(underlying)}</div>
          </div>
          <div className="bg-gray-800/50 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Premium</div>
            <div className="text-sm font-semibold text-white">{fmtPrice(premium)}</div>
          </div>
          <div className="bg-gray-800/50 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Breakeven</div>
            <div className="text-sm font-semibold text-white">{fmtPrice(breakeven)}</div>
          </div>
          <div className="bg-gray-800/50 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Days to Exit</div>
            <div className="text-sm font-semibold text-white">{fmtDays(dte)}</div>
          </div>
        </div>
      )}

      {/* When the snapshot is hidden (mobile), still show Breakeven inline since
          the identity block doesn't include it */}
      {hideSnapshot && breakeven != null && (
        <div className="bg-gray-800/50 rounded px-3 py-2 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wide text-gray-400">Breakeven</span>
          <span className="text-sm font-semibold text-white">{fmtPrice(breakeven)}</span>
        </div>
      )}

      {/* Greeks + Market Data + (Sell Metrics for sell positions) */}
      <GreeksMarketSection contract={contractAdapter} simulationData={rowData} />
    </div>
  );
}
